import type {
  AddDatabaseItemsInput,
  CreateTableViewChatRequest,
  CreateTableViewColumnInput,
  ExecuteAnalysisInput,
  GenerateTableViewColumnsInput,
  GetTableViewResponse,
  InitializeFromProductCodeInput,
  LinkFilesInput,
  RegenerateCellInput,
  RegisterTableViewFileOnlyRequest,
  ReorderTableViewColumnsBody,
  TableViewFileInfo,
  TableViewFilters,
  TableViewRegisterFileRequest,
  TestDatabaseQueryInput,
  UnlinkFilesInput,
  UpdateTableViewCellInput,
  UpdateTableViewColumnInput,
  UpdateTableViewInput,
  UseTemplateInput,
} from '@ditto/shared';
import { CreateTableViewInput, DatabaseSource } from '@ditto/shared';
import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useAuthenticatedClient, useProtectedQuery } from './useApi';

export const queryKeys = {
  all: ['tableViews'],
  list: () => [...queryKeys.all, 'list'],
  detail: (id: string) => [...queryKeys.all, 'detail', id],
  chat: (tableViewId: string) => [...queryKeys.all, 'chat', tableViewId],
  databaseSources: () => [...queryKeys.all, 'databaseSources'],
  databaseQueries: (tableViewId: string) => [
    ...queryKeys.all,
    'databaseQueries',
    tableViewId,
  ],
  workspaceFiles: () => [...queryKeys.all, 'workspaceFiles'],
  linkedFiles: (tableViewId: string) => [
    ...queryKeys.all,
    'linkedFiles',
    tableViewId,
  ],
};

export function useListTableViewsApi() {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.list(),
    queryFn: async () => {
      const response = await client.tableView.listTableViews();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch table views');
    },
  });
}

export function useGetTableViewApi(
  id: string,
  filters?: TableViewFilters,
  options?: {
    refetchInterval?: UseQueryOptions<
      GetTableViewResponse,
      Error,
      GetTableViewResponse,
      unknown[]
    >['refetchInterval'];
  }
) {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: [...queryKeys.detail(id), filters],
    queryFn: async () => {
      const response = await client.tableView.getTableView({
        params: { id },
        query: filters ?? {},
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch table view');
    },
    enabled: !!id,
    refetchInterval: options?.refetchInterval,
    // Keep showing previous data while new filtered data is loading
    placeholderData: keepPreviousData,
  });
}

export function useGetTableViewPresignedUrlsApi() {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      files,
    }: {
      tableViewId: string;
      files: TableViewFileInfo[];
    }) => {
      const response = await client.tableView.getPresignedUrls({
        params: { id: tableViewId },
        body: { files },
      });
      if (response.status === 200) {
        return response.body.uploadUrls;
      }
      throw new Error('Failed to get presigned URLs');
    },
    onError: (error: Error) => {
      console.error('Failed to get presigned URLs:', error);
    },
  });
}

export function useRegisterTableViewFilesApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tableViewId,
      files,
    }: {
      tableViewId: string;
      files: TableViewRegisterFileRequest[];
    }) => {
      const response = await client.tableView.registerFiles({
        params: { id: tableViewId },
        body: { files },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to register files');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
    onError: (error: Error) => {
      console.error('Failed to register files:', error);
    },
  });
}

export function useCreateTableViewApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTableViewInput) => {
      const response = await client.tableView.createTableView({ body: data });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create table view');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.list() });
    },
  });
}

export function useUpdateTableViewApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: UpdateTableViewInput;
    }) => {
      const response = await client.tableView.updateTableView({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update table view');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.list(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
      queryClient.invalidateQueries({
        queryKey: ['projects'],
      });
    },
  });
}

export function useDeleteTableViewApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableViewId: string) => {
      const response = await client.tableView.deleteTableView({
        params: { id: tableViewId },
      });
      if (response.status === 200) {
        return;
      }
      if (response.status === 404) {
        throw new Error(response.body.message);
      }
      throw new Error('Failed to delete table view');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.list(),
      });
    },
  });
}

export function useGenerateTableViewColumnsApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: GenerateTableViewColumnsInput;
    }) => {
      const response = await client.tableView.generateTableViewColumns({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to generate table view columns');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
      // Also refresh list since name may have been auto-generated
      queryClient.invalidateQueries({
        queryKey: queryKeys.list(),
      });
    },
  });
}

export function useCreateTableViewColumnApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTableViewColumnInput) => {
      const response = await client.tableView.createTableViewColumn({
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create table view column');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}

export function useUpdateTableViewColumnApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      columnId,
      data,
    }: {
      columnId: string;
      tableViewId: string;
      data: UpdateTableViewColumnInput;
    }) => {
      const response = await client.tableView.updateTableViewColumn({
        params: { columnId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update table view column');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}

export function useDeleteTableViewColumnApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      columnId,
    }: {
      columnId: string;
      tableViewId: string;
    }) => {
      const response = await client.tableView.deleteTableViewColumn({
        params: { columnId },
        body: undefined,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete table view column');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}

export function useExecuteTableViewAnalysisApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: ExecuteAnalysisInput;
    }) => {
      const response = await client.tableView.executeAnalysis({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to execute table view analysis');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}

export function useGetFileApi(fileId: string | undefined) {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: ['file', fileId],
    queryFn: fileId
      ? async () => {
          const response = await client.file.getFile({
            params: { id: fileId },
            query: {},
          });
          if (response.status === 200) {
            return response.body;
          }
          throw new Error('Failed to fetch file');
        }
      : skipToken,
  });
}

export function useExportTableViewApi() {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async ({ tableViewId }: { tableViewId: string }) => {
      const response = await client.tableView.exportTableView({
        params: { id: tableViewId },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to export table view');
    },
  });
}

export function useInitializeFromProductCodeApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: InitializeFromProductCodeInput;
    }) => {
      const response = await client.tableView.initializeFromProductCode({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to initialize from product code');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}

export function useDeleteTableViewRowsApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      rowIds,
    }: {
      tableViewId: string;
      rowIds: string;
    }) => {
      const response = await client.tableView.deleteTableViewRows({
        params: { id: tableViewId },
        body: { rowIds },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete table view rows');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}

export function useGetTemplatesApi() {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const response = await client.tableView.getTemplates();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch templates');
    },
  });
}

export function useUseTemplateApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UseTemplateInput) => {
      const response = await client.tableView.useTemplate({ body: data });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create table view from template');
    },
    onSuccess: async () => {
      // Invalidate all table view queries to ensure the list is refreshed
      await queryClient.invalidateQueries({ queryKey: queryKeys.all });
    },
  });
}

export function useCreateTableViewChatApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTableViewChatRequest) => {
      const response = await client.tableView.createTableViewChat({
        params: { tableViewId: data.tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create table view chat');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat(variables.tableViewId),
      });
    },
  });
}

export function useGetTableViewChatApi(tableViewId: string) {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.chat(tableViewId),
    queryFn: async () => {
      const response = await client.tableView.getTableViewChat({
        params: { tableViewId },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch table view chat');
    },
    enabled: !!tableViewId,
  });
}

// Database sources API hooks
export function useListDatabaseSourcesApi() {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.databaseSources(),
    queryFn: async () => {
      const response = await client.tableView.listDatabaseSources();
      if (response.status === 200) {
        return response.body.sources;
      }
      throw new Error('Failed to fetch database sources');
    },
  });
}

export function useTestDatabaseQueryApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: TestDatabaseQueryInput;
    }) => {
      const response = await client.tableView.testDatabaseQuery({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to test database query');
    },
    onSuccess: (_data, variables) => {
      // Invalidate saved queries to refresh the list with new result count
      queryClient.invalidateQueries({
        queryKey: queryKeys.databaseQueries(variables.tableViewId),
      });
    },
  });
}

export function useAddDatabaseItemsApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: AddDatabaseItemsInput;
    }) => {
      const response = await client.tableView.addDatabaseItems({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to add database items');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.databaseQueries(variables.tableViewId),
      });
      // Also refresh list since name may have been auto-generated
      queryClient.invalidateQueries({
        queryKey: queryKeys.list(),
      });
    },
  });
}

// Saved database queries hooks
export function useListDatabaseQueriesApi(tableViewId: string | undefined) {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.databaseQueries(tableViewId ?? ''),
    queryFn: async () => {
      if (!tableViewId) {
        throw new Error('Table view ID is required');
      }
      const response = await client.tableView.listDatabaseQueries({
        params: { id: tableViewId },
      });
      if (response.status === 200) {
        return response.body.queries;
      }
      throw new Error('Failed to fetch database queries');
    },
    enabled: !!tableViewId,
  });
}

export function useSetDatabaseActiveApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: { source: DatabaseSource; isActive: boolean };
    }) => {
      const response = await client.tableView.setDatabaseActive({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update database active state');
    },
    onSuccess: (_, { tableViewId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.databaseQueries(tableViewId),
      });
    },
  });
}

// Workspace files API hooks
export function useListWorkspaceFilesApi() {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.workspaceFiles(),
    queryFn: async () => {
      const response = await client.tableView.listWorkspaceTableViewFiles();
      if (response.status === 200) {
        return response.body.files;
      }
      throw new Error('Failed to fetch workspace files');
    },
  });
}

export function useListLinkedFilesApi(tableViewId: string | undefined) {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.linkedFiles(tableViewId ?? ''),
    queryFn: async () => {
      if (!tableViewId) {
        throw new Error('Table view ID is required');
      }
      const response = await client.tableView.listLinkedFiles({
        params: { id: tableViewId },
      });
      if (response.status === 200) {
        return response.body.files;
      }
      throw new Error('Failed to fetch linked files');
    },
    enabled: !!tableViewId,
  });
}

export function useRegisterTableViewFileOnlyApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RegisterTableViewFileOnlyRequest) => {
      const response = await client.tableView.registerTableViewFileOnly({
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to register file');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workspaceFiles(),
      });
    },
  });
}

// General file presigned URLs (for workspace-level uploads)
export function useFilePresignedUrlsApi() {
  const { client } = useAuthenticatedClient();

  return useMutation({
    mutationFn: async (files: TableViewFileInfo[]) => {
      const response = await client.file.getPresignedUrls({
        body: { files },
      });
      if (response.status === 200) {
        return response.body.uploadUrls;
      }
      throw new Error('Failed to get presigned URLs');
    },
    onError: (error: Error) => {
      console.error('Failed to get presigned URLs:', error);
    },
  });
}

export function useLinkFilesApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: LinkFilesInput;
    }) => {
      const response = await client.tableView.linkFiles({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to link files');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.linkedFiles(variables.tableViewId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workspaceFiles(),
      });
      // Also refresh list since name may have been auto-generated
      queryClient.invalidateQueries({
        queryKey: queryKeys.list(),
      });
    },
  });
}

export function useUnlinkFilesApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: UnlinkFilesInput;
    }) => {
      const response = await client.tableView.unlinkFiles({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to unlink files');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.linkedFiles(variables.tableViewId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.workspaceFiles(),
      });
    },
  });
}

export function useUpdateTableViewCellApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: UpdateTableViewCellInput;
    }) => {
      const response = await client.tableView.updateTableViewCell({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update cell');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}

export function useReorderTableViewColumnsApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: ReorderTableViewColumnsBody;
    }) => {
      const response = await client.tableView.reorderTableViewColumns({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to reorder columns');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}

export function useRegenerateCellApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableViewId,
      data,
    }: {
      tableViewId: string;
      data: RegenerateCellInput;
    }) => {
      const response = await client.tableView.regenerateCell({
        params: { id: tableViewId },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to regenerate cell');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.tableViewId),
      });
    },
  });
}
